/**
 * errorLogger.js — captura global de errores JS de cliente.
 *
 * Complementa al `ErrorBoundary` de React (que sólo intercepta errores del
 * render tree). Este módulo captura:
 *   - `window.onerror`: excepciones síncronas fuera del render (event
 *     handlers, timers, callbacks…).
 *   - `unhandledrejection`: promesas rechazadas sin `.catch()` (fetch/axios).
 *
 * Publica los errores al backend en `POST /api/error-log` de forma silenciosa
 * (best-effort, nunca revienta al usuario). Aplica un rate-limit por
 * fingerprint (message + primera línea del stack) para no inundar la BBDD si
 * un error se dispara en bucle dentro de un componente.
 */
import api from "./api";

const _seen = new Map(); // fingerprint -> lastSentAt (ms)
const RATE_LIMIT_MS = 30_000; // no reenviar el mismo error más de 1 vez cada 30 s
const MAX_ENTRIES = 200;      // salvaguarda memoria

function fingerprint(message, stack) {
  const first = (stack || "").split("\n").slice(0, 1).join("");
  return `${message || ""}::${first}`.slice(0, 400);
}

function shouldSkip(message, stack) {
  const now = Date.now();
  const fp = fingerprint(message, stack);
  const last = _seen.get(fp);
  if (last && (now - last) < RATE_LIMIT_MS) return true;
  // GC básico
  if (_seen.size > MAX_ENTRIES) {
    const cutoff = now - RATE_LIMIT_MS;
    for (const [k, t] of _seen.entries()) {
      if (t < cutoff) _seen.delete(k);
    }
  }
  _seen.set(fp, now);
  return false;
}

/**
 * Publica un error al backend. No hace throw; los fallos de red se ignoran.
 * @param {Error|string} err
 * @param {object} [extra] - metadatos adicionales (component, action…)
 */
export function logClientError(err, extra = {}) {
  const message = (err && err.message) ? err.message : String(err);
  const stack = (err && err.stack) ? err.stack : "";
  if (shouldSkip(message, stack)) return;
  try {
    api.post("/error-log", {
      message: message.slice(0, 2000),
      stack: stack.slice(0, 8000),
      component_stack: extra?.component_stack ? String(extra.component_stack).slice(0, 4000) : "",
      url: (typeof window !== "undefined" && window.location) ? window.location.href : "",
      user_agent: (typeof navigator !== "undefined") ? navigator.userAgent : "",
    }).catch(() => {});
  } catch { /* noop */ }
}

let _installed = false;

/**
 * Instala los handlers globales UNA sola vez. Idempotente.
 * Debe llamarse muy pronto en el arranque (index.js), antes del primer render.
 */
export function installGlobalErrorHandlers() {
  if (_installed || typeof window === "undefined") return;
  _installed = true;

  window.addEventListener("error", (event) => {
    const err = event?.error || new Error(event?.message || "window.onerror");
    logClientError(err, { component_stack: `source=${event?.filename || "?"}:${event?.lineno || "?"}:${event?.colno || "?"}` });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    let err;
    if (reason instanceof Error) {
      err = reason;
    } else if (reason && typeof reason === "object") {
      // Axios-style: interesa el status/URL para debug
      const msg = reason?.message || reason?.detail || JSON.stringify(reason).slice(0, 500);
      err = new Error(`UnhandledRejection: ${msg}`);
      err.stack = reason?.stack || "";
    } else {
      err = new Error(`UnhandledRejection: ${String(reason)}`);
    }
    logClientError(err, { component_stack: "unhandledrejection" });
  });
}
